import React, { useEffect, useMemo, useRef } from "react";
import { useReducedMotion } from "../../hooks/useReducedMotion.hook";
import { getUIColorPalette } from "../../theme/ColorPalette";
import { hexToRgb } from "../../tools/ColorTools";
import { getItineraryLegColorRgba } from "../../tools/TransportModeColorsTools";
import {
    getTransportModeGroupForItineraryLeg,
    isTransportModeInGroups,
    TransportModeGroup,
} from "../../tools/TransportModeGroupsTools";
import { type ItineraryDTO, type ItineraryLegDTO } from "../../types/dtos/api/itinerary/itinerary.dto";
import { type PurchasableSegmentDTO } from "../../types/dtos/api/purchase/purchase-segments.dto";
import { getLegRouteShortName } from "../ItinerariesPlanner/itineraryDetails/itineraryDetails.tools";
import ItineraryTransportIcon from "../ItinerariesPlanner/shared/ItineraryTransportIcon";
import {
    buildTicketSegmentProgressUnits,
    resolvePurchasableSegmentIndexByLegIndex,
    type TicketSegmentProgressUnit,
} from "./ticketSegmentProgress.tools";

interface TicketSegmentTravelProgressProps {

    /** Whether dark UI palette is active. */
    "isDarkUi": boolean;

    /** Itinerary being purchased (legs + pricing segments + leg index lookup). */
    "itinerary": ItineraryDTO | null;

    /** Ordered purchasable segments, in the same order as the selection steps. */
    "purchasableSegments": PurchasableSegmentDTO[];

    /** Position inside `purchasableSegments` of the segment currently being configured. */
    "activeSegmentIndex": number;
}

/** Connector alpha for legs inside purchasable segment groups. */
const GROUP_LEG_STRIP_OPACITY = 0.85;

/** Connector alpha for legs outside purchasable segments. */
const UNTRACKED_LEG_STRIP_OPACITY = 0.3;

/** Group opacity applied to a purchasable segment already configured. */
const DONE_GROUP_OPACITY = 0.5;

/** Saturation factor applied to an already configured segment so it reads as grayed out. */
const DONE_GROUP_SATURATION = 0.35;

/** Group opacity applied to a purchasable segment still to be configured. */
const UPCOMING_GROUP_OPACITY = 0.45;

/** Opacity applied to a bare leg outside every purchasable segment. */
const UNTRACKED_LEG_OPACITY = 0.3;

/** Width (px) of the transport badge rendered by ItineraryTransportIcon's badge variant. */
const LEG_BADGE_WIDTH_PX = 40;

/** Minimum connector width (px) for a leg without route badge. */
const MIN_LEG_CONNECTOR_WIDTH_PX = 24;

/** Minimum connector width (px) when the leg carries a route badge, so the pill stays inside. */
const MIN_LEG_CONNECTOR_WIDTH_WITH_BADGE_PX = 44;

/** Extra width (px) added to a segment group minimum width for border and padding. */
const GROUP_CHROME_WIDTH_PX = 16;

/** Background alpha of the active segment group (light / dark UI). */
const ACTIVE_GROUP_BACKGROUND_ALPHA_LIGHT = 0.1;
const ACTIVE_GROUP_BACKGROUND_ALPHA_DARK = 0.16;

/** Purchase-position state of one segment group. */
type SegmentGroupVisualState = "active" | "done" | "upcoming";

/** Resolves the visual state of a segment group from its position relative to the active one. */
const resolveSegmentGroupVisualState = (
    segmentPosition: number,
    activeSegmentIndex: number,
): SegmentGroupVisualState => {
    if ( segmentPosition === activeSegmentIndex ) {
        return "active";
    }

    return segmentPosition < activeSegmentIndex ? "done" : "upcoming";
};

/** Walk and bike legs are removed from the purchase progress bar. */
const isHiddenLegModeGroup = ( leg: ItineraryLegDTO ): boolean => {
    const modeGroup = getTransportModeGroupForItineraryLeg( leg );

    return modeGroup === TransportModeGroup.WALK || modeGroup === TransportModeGroup.BICYCLE;
};

/** Safe duration in seconds for flex-grow proportionality (clamps missing/invalid values to 0). */
const legDurationSeconds = ( leg: ItineraryLegDTO ): number => {
    const raw = leg.duration;

    if ( typeof raw !== "number" || Number.isNaN( raw ) || raw < 0 ) {
        return 0;
    }

    return raw;
};

/** Transit legs expose a stop id; route badges stay off soft modes, carpool, and boat-shuttle legs. */
const shouldShowLegRouteBadge = ( leg: ItineraryLegDTO ): boolean => {
    return leg.from?.stop?.id != null &&
      !isTransportModeInGroups( leg.mode, [
          TransportModeGroup.WALK,
          TransportModeGroup.BICYCLE,
          TransportModeGroup.RENTAL,
          TransportModeGroup.BLABLADAILY,
          TransportModeGroup.BLABLACAR,
          TransportModeGroup.HE_LEMAN,
      ] );
};

/** Minimum width (px) of one leg entry: badge plus a connector wide enough for its route badge. */
const legUnitMinWidth = ( leg: ItineraryLegDTO ): number => {
    const connectorMinWidth = shouldShowLegRouteBadge( leg )
        ? MIN_LEG_CONNECTOR_WIDTH_WITH_BADGE_PX
        : MIN_LEG_CONNECTOR_WIDTH_PX;

    return LEG_BADGE_WIDTH_PX + connectorMinWidth;
};

/**
 * TravelProgress-like bar for ticket segment selection: renders the whole journey
 * (minus walk and bike legs) as one bordered group per purchasable segment and
 * highlights the segment currently being configured.
 *
 * @remarks
 * - Each purchasable segment renders as a rounded bordered group containing its
 *   leg transport badges and duration-proportional colored connectors (with the
 *   route short name inside). The group under selection gets a brand border and
 *   a brand-tinted background; already configured groups are grayed out; groups
 *   still to configure are dimmed; legs outside all purchasable segments render
 *   bare and dimmed.
 * - The bar is display-only: navigation stays on the Retour/Continuer buttons.
 *
 * @param props Component props.
 * @returns The segment progress bar, or `null` when no legs can be rendered.
 */
export const TicketSegmentTravelProgress: React.FC<TicketSegmentTravelProgressProps> = ( {
    isDarkUi,
    itinerary,
    purchasableSegments,
    activeSegmentIndex,
} ) => {
    const palette = getUIColorPalette( isDarkUi );
    const reducedMotion = useReducedMotion();
    const activeUnitRef = useRef<HTMLDivElement | null>( null );

    const purchasableSegmentIds = useMemo(
        () => purchasableSegments.map( ( segment ) => segment.segmentId ),
        [ purchasableSegments ],
    );

    const positionByLegIndex = useMemo(
        () => ( itinerary === null
            ? new Map<number, number>()
            : resolvePurchasableSegmentIndexByLegIndex( itinerary, purchasableSegmentIds ) ),
        [ itinerary, purchasableSegmentIds ],
    );

    const visibleLegs = useMemo(
        () => ( itinerary?.legs ?? [] ).
            map( ( leg, legIndex ) => ( {
                leg,
                legIndex,
            } ) ).
            filter( ( { leg } ) => !isHiddenLegModeGroup( leg ) ),
        [ itinerary ],
    );

    const units = useMemo(
        () => buildTicketSegmentProgressUnits( visibleLegs, positionByLegIndex ),
        [ visibleLegs, positionByLegIndex ],
    );

    const totalVisibleDuration = useMemo(
        () => visibleLegs.reduce( ( sum, { leg } ) => sum + legDurationSeconds( leg ), 0 ),
        [ visibleLegs ],
    );

    const activeGroupBackground = useMemo( () => {
        const { r, g, b } = hexToRgb( palette.brandPrimary );

        return `rgba(${ r }, ${ g }, ${ b }, ${ isDarkUi ? ACTIVE_GROUP_BACKGROUND_ALPHA_DARK : ACTIVE_GROUP_BACKGROUND_ALPHA_LIGHT })`;
    }, [ isDarkUi, palette.brandPrimary ] );

    // Keeps the active segment group visible when the bar overflows horizontally.
    useEffect( () => {
        activeUnitRef.current?.scrollIntoView( {
            "block":    "nearest",
            "inline":   "center",
            "behavior": reducedMotion ? "auto" : "smooth",
        } );
    }, [ activeSegmentIndex, reducedMotion ] );

    if ( visibleLegs.length === 0 ) {
        return null;
    }

    const renderConnector = ( leg: ItineraryLegDTO, stripOpacity: number ) => (
        <div
            className={`ml-0.5 flex h-5 ${ shouldShowLegRouteBadge( leg ) ? "min-w-10" : "min-w-6" } flex-1 items-center justify-center overflow-hidden rounded-full`}
            style={{ "backgroundColor": getItineraryLegColorRgba( leg, stripOpacity ) }}
        >
            {shouldShowLegRouteBadge( leg ) && (
                <p className="flex h-4 min-w-8 items-center justify-center rounded-md border border-white bg-ionic-primary p-1 text-[10px] font-bold leading-none text-white">
                    {getLegRouteShortName( leg ).slice( 0, 5 )}
                </p>
            )}
        </div>
    );

    const renderUnit = ( unit: TicketSegmentProgressUnit ) => {
        const unitDuration = unit.legs.reduce( ( sum, { leg } ) => sum + legDurationSeconds( leg ), 0 );
        const unitGrow = totalVisibleDuration > 0 ? Math.max( unitDuration, 1 ) : 1;
        const minWidth = unit.legs.reduce( ( sum, { leg } ) => sum + legUnitMinWidth( leg ), 0 ) +
          ( unit.kind === "segment" ? GROUP_CHROME_WIDTH_PX : 0 );

        if ( unit.kind === "untracked" ) {
            const { leg } = unit.legs[0];

            return (
                <div
                    className="flex min-w-0 items-center"
                    key={leg.id}
                    role="listitem"
                    style={{
                        "flexBasis": 0,
                        "flexGrow":  unitGrow,
                        "minWidth":  minWidth,
                        "opacity":   UNTRACKED_LEG_OPACITY,
                        "filter":    "saturate(0.5)",
                    }}
                >
                    <div className="relative shrink-0">
                        {ItineraryTransportIcon(
                            leg.mode,
                            false,
                            "badge",
                            leg.rental?.vehicle?.type,
                            leg,
                        )}
                    </div>
                    {renderConnector( leg, UNTRACKED_LEG_STRIP_OPACITY )}
                </div>
            );
        }

        const segmentPosition = unit.segmentPosition ?? 0;
        const state = resolveSegmentGroupVisualState( segmentPosition, activeSegmentIndex );

        return (
            <div
                aria-current={state === "active" ? "step" : undefined}
                aria-label={`Billet ${ segmentPosition + 1 } sur ${ purchasableSegments.length }`}
                className="flex min-w-0 items-center rounded-xl border-2 px-1.5 py-1"
                key={`segment-${ unit.segmentPosition }`}
                ref={state === "active" ? activeUnitRef : undefined}
                role="listitem"
                style={{
                    "flexBasis":       0,
                    "flexGrow":        unitGrow,
                    "minWidth":        minWidth,
                    "borderColor":     state === "active" ? palette.brandPrimary : palette.overlayBorderStrong,
                    "backgroundColor": state === "active" ? activeGroupBackground : undefined,
                    "opacity":         state === "done"
                        ? DONE_GROUP_OPACITY
                        : state === "upcoming" ? UPCOMING_GROUP_OPACITY : 1,
                    "filter": state === "done" ? `saturate(${ DONE_GROUP_SATURATION })` : undefined,
                }}
            >
                {unit.legs.map( ( { leg } ) => (
                    <div
                        className="flex min-w-0 flex-1 items-center"
                        key={leg.id}
                        style={{
                            "flexBasis": 0,
                            "flexGrow":  Math.max( legDurationSeconds( leg ), 1 ),
                            "minWidth":  legUnitMinWidth( leg ),
                        }}
                    >
                        <div className="relative shrink-0">
                            {ItineraryTransportIcon(
                                leg.mode,
                                false,
                                "badge",
                                leg.rental?.vehicle?.type,
                                leg,
                            )}
                        </div>
                        {renderConnector( leg, GROUP_LEG_STRIP_OPACITY )}
                    </div>
                ) )}
            </div>
        );
    };

    return (
        <div
            aria-label={`Billets : segment ${ activeSegmentIndex + 1 } sur ${ purchasableSegments.length }`}
            className="flex w-full min-w-0 flex-row items-center gap-1.5 overflow-x-auto overflow-y-hidden"
            role="list"
        >
            {units.map( renderUnit )}
        </div>
    );
};

export default TicketSegmentTravelProgress;
